<template>
    <transition name="modal">
    <div class="dialog-modal-mask">
    <div class="small-panel" @click.stop>
        <div class="dialog-title" v-t="'message.title_summary_generate'"></div>
        <hr class="dialog-hr">
        <div class="dialog-body-small">
            <div class="summary-generate-row">
                <span class="summary-generate-label" v-t="'message.item_summary_prompt'"></span>
                <el-select
                    v-model="selectedPromptId"
                    size="small"
                    style="width:280px"
                    :disabled="promptList.length === 0"
                    @change="savePromptId()">
                    <el-option v-for="prompt in promptList" :key="prompt.id" :label="prompt.name" :value="prompt.id"></el-option>
                </el-select>
            </div>

            <div class="summary-generate-row">
                <span class="summary-generate-label" v-t="'message.item_summary_prompt_preview'"></span>
                <pre class="summary-generate-preview">{{ promptPreview }}</pre>
            </div>

            <div class="summary-generate-row">
                <span class="summary-generate-label" v-t="'message.item_summary_period'"></span>
                <span class="summary-generate-value">{{ periodText }}</span>
            </div>

            <div class="summary-generate-row">
                <span class="summary-generate-label" v-t="'message.item_summary_target'"></span>
                <span class="summary-generate-value">{{ targetText }}</span>
            </div>
        </div>
        <hr class="dialog-hr">
        <div class="dialog-button-field summary-dialog-button-field">
            <el-button @click="$emit('close')" v-t="'message.action_close'"></el-button>
            <el-button class="dialog-ok-button" :disabled="!canGenerate" @click="generate()" v-t="'message.action_summary_generate'"></el-button>
        </div>
    </div>
    </div>
    </transition>
</template>

<script>
// 実装: poc/geniac ブランチの SummaryGenerateDialog.vue をベースに、UIレビューの2点を反映。
// 1) 言語指定UIは撤去（今コンソールに表示中の言語だけで生成する。languageIndex は親から渡された値をそのまま使う）
// 2) プロンプトのプレビューは先頭100文字に切り詰めていたが、スクロールで全文を確認できるようにした
// 一覧取得（SummaryHistoryList）は ai サーバーへ直接 aiGet するが、生成もAIサーバーへ直接SSEを張る点は同じ。
// 生成リクエスト自体をこのダイアログの寿命に縛ると「生成中は他の操作ができない」体験になるため、
// このコンポーネントは入力内容から request body を組み立てて親（Talk.vue）へ渡すところまでに専念する。
// 実際の server.aiSse 呼び出しと進行状況表示（画面右下のポップアップ）は Talk.vue 側が持つ
import server from "../ServerApi.js";
import commonUtil from "../commonutil.js";

const SELECTED_PROMPT_STORAGE_KEY = "consoleSummarySelectedPromptId";

export default {
    props: {
        appData: { type: Object, default: () => ({}) },
        selected: { type: Array, default: () => [] },
        isTalkgroup: { type: Boolean, default: false },
        // 発話履歴で選択中の言語。要約もこの言語のテキストを対象にする（ダイアログ内での変更はできない）
        languageIndex: { type: Number, default: 0 },
        // 発話履歴でチェックされている範囲 { chatMinId, chatMaxId, count, fromTime, toTime }
        selection: { type: Object, default: null },
    },
    data() {
        return {
            promptList: [],
            selectedPromptId: null,
            aiServerBaseUrl: "",
        };
    },
    computed: {
        targetNode() {
            return this.selected.length > 0 ? this.selected[0] : null;
        },
        loginTenantId() {
            if (this.appData && this.appData.tenantList && this.appData.tenantList.length > 0) {
                return this.appData.tenantList[0].tenantId;
            }
            return 0;
        },
        // 連携先テナントのグループは自テナントの発話DBから読めないため要約対象にしない
        isOwnTenantGroup() {
            if (!this.targetNode) return false;
            const nodeTenantId = this.targetNode.data.tenantId;
            return !nodeTenantId || nodeTenantId === this.loginTenantId;
        },
        targetCount() {
            return this.selection ? this.selection.count : 0;
        },
        targetText() {
            return commonUtil.getMessageArgsText(this.$i18n, "item_summary_target_count", { count: this.targetCount });
        },
        // 対象期間はチェックした範囲の最初と最後の発話時刻（検索期間ではない）
        periodText() {
            if (!this.selection || !this.selection.fromTime || !this.selection.toTime) {
                return commonUtil.getMessageText(this.$i18n, "item_summary_unknown");
            }
            return `${this.selection.fromTime.toLocaleString()} - ${this.selection.toTime.toLocaleString()}`;
        },
        promptPreview() {
            const prompt = this.promptList.find((item) => item.id === this.selectedPromptId);
            return prompt ? String(prompt.prompt || "").trim() : "";
        },
        canGenerate() {
            return this.isTalkgroup
                && this.isOwnTenantGroup
                && !!this.selectedPromptId
                && this.targetCount > 0;
        },
    },
    async mounted() {
        try {
            await this.loadAiServerUrl();
            await this.loadPromptList();
        } catch (e) {
            commonUtil.putExceptionErrorMessage(this, "loadSummaryPrompts", e);
        }
    },
    methods: {
        normalizeAiServerUrl(rawUrl) {
            const trimmed = String(rawUrl || "").replace(/\/+$/, "");
            if (!trimmed) {
                throw new Error("empty ai server url");
            }
            if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
                return trimmed;
            }
            return `https://${trimmed}`;
        },
        async loadAiServerUrl() {
            const res = await server.post("/getaiserverurl", {});
            if (res.data.status !== 0) {
                throw new Error(`getaiserverurl failed status=${res.data.status}`);
            }
            this.aiServerBaseUrl = this.normalizeAiServerUrl(res.data.aiServerUrl);
        },
        async loadPromptList() {
            if (!this.aiServerBaseUrl || !this.loginTenantId) {
                return;
            }
            const res = await server.aiGet(this.aiServerBaseUrl, "/console/summary/prompt/list", this.loginTenantId);
            this.promptList = (res.data.promptList || []).map((prompt) => ({
                id: prompt.id,
                name: prompt.name || commonUtil.getMessageArgsText(this.$i18n, "item_ai_prompt_name_with_id", { id: prompt.id }),
                prompt: prompt.prompt,
            }));
            this.selectedPromptId = this.restorePromptId();
        },
        restorePromptId() {
            const saved = Number(window.localStorage.getItem(SELECTED_PROMPT_STORAGE_KEY));
            const restored = this.promptList.find((prompt) => prompt.id === saved);
            if (restored) {
                return restored.id;
            }
            return this.promptList.length > 0 ? this.promptList[0].id : null;
        },
        savePromptId() {
            if (this.selectedPromptId) {
                window.localStorage.setItem(SELECTED_PROMPT_STORAGE_KEY, String(this.selectedPromptId));
            }
        },
        buildRequestBody() {
            return {
                groupId: this.targetNode.data.id,
                promptId: this.selectedPromptId,
                interestLanguageIndex: this.languageIndex,
                chatMinId: this.selection.chatMinId,
                chatMaxId: this.selection.chatMaxId,
            };
        },
        // 実際の生成リクエストは投げず、親（Talk.vue）へ内容を渡して閉じるだけ。
        // 生成の進行はモーダルでなく画面右下のポップアップが引き継ぐ
        generate() {
            if (!this.canGenerate) {
                return;
            }
            if (!this.aiServerBaseUrl) {
                commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_summary_failed"));
                return;
            }
            this.$emit("start-generate", {
                aiServerBaseUrl: this.aiServerBaseUrl,
                tenantId: this.loginTenantId,
                requestBody: this.buildRequestBody(),
            });
        },
    },
};
</script>

<style scoped>
.summary-dialog-button-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 20px;
}

.summary-generate-row {
    font-size: 13px;
    display: flex;
    align-items: flex-start;
    margin-bottom: 12px;
}
.summary-generate-row:last-child {
    margin-bottom: 0;
}
.summary-generate-label {
    flex: 0 0 120px;
    color: #848991;
    line-height: 32px;
}
.summary-generate-value {
    line-height: 32px;
}
.summary-generate-preview {
    flex: 1;
    min-width: 0;
    max-height: 160px;
    overflow-y: auto;
    margin: 0;
    padding: 8px;
    box-sizing: border-box;
    background-color: #f6f6f6;
    border: 1px solid #eeeeee;
    border-radius: 4px;
    color: #333b48;
    font-family: inherit;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
}
</style>
